/**
 * Onboarding Modal Component
 * Handles learning language and CEFR level selection for new users
 */

import { logEvent } from '../../core/event-logger.js';
import { EVENT_TYPES } from '../../core/event-types.js';
import appState from '../../core/app-state.js';
import type { DifficultyLevel } from '../../types/clip.js';

/**
 * Callback functions type
 */
interface OnboardingCallbacks {
  savePreferences: () => void;
  generateDailySet: (source: 'auto' | 'manualShuffle' | 'onboarding') => void;
  renderLibraryView: () => void;
}

// Dependencies passed from main.js
let savePreferences: (() => void) | undefined;
let generateDailySet: ((source: 'auto' | 'manualShuffle' | 'onboarding') => void) | undefined;
let renderLibraryView: (() => void) | undefined;

/**
 * Register external dependencies
 */
export function registerOnboardingCallbacks(callbacks: OnboardingCallbacks): void {
  savePreferences = callbacks.savePreferences;
  generateDailySet = callbacks.generateDailySet;
  renderLibraryView = callbacks.renderLibraryView;
}

/**
 * Get list of unique languages from clips data
 */
function getAvailableLanguages(): string[] {
  if (!appState.clips || appState.clips.length === 0) {
    return ['tr', 'de']; // Default fallback: Turkish and German
  }
  
  const languages = new Set<string>();
  appState.clips.forEach(clip => {
    if (clip.language) {
      languages.add(clip.language);
    }
  });
  
  // Always include Turkish and German as supported languages
  languages.add('tr');
  languages.add('de');
  
  return Array.from(languages).sort();
}

/**
 * Get language display names
 */
function getLanguageDisplayName(langCode: string): string {
  const names: Record<string, string> = {
    'tr': 'Türkçe (Turkish)',
    'de': 'Deutsch (German)',
    'en': 'English',
    'es': 'Español (Spanish)',
    'fr': 'Français (French)',
    'it': 'Italiano (Italian)'
  };
  return names[langCode] || langCode.toUpperCase();
}

/**
 * Show onboarding modal - Step 1: Select learning language
 */
export function showOnboardingModal(): void {
  const eventType = EVENT_TYPES['ONBOARDING_SHOWN'];
  if (eventType) {
    logEvent(eventType, {});
  }
  
  // Create modal overlay
  const overlay = document.createElement('div');
  overlay.id = 'onboarding-overlay';
  overlay.style.cssText = 'position: fixed; top: 0; left: 0; right: 0; bottom: 0; background-color: rgba(0, 0, 0, 0.5); z-index: 9999; display: flex; align-items: center; justify-content: center; padding: 1rem;';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-labelledby', 'onboarding-title');
  
  // Create modal content
  const modal = document.createElement('div');
  modal.style.cssText = 'background-color: white; border-radius: 0.5rem; padding: 2rem; max-width: 500px; width: 100%; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);';
  
  const title = document.createElement('h2');
  title.id = 'onboarding-title';
  title.textContent = 'Which language do you want to learn?';
  title.style.cssText = 'font-size: 1.5rem; font-weight: 600; margin-bottom: 1.5rem; color: #1f2937;';
  modal.appendChild(title);
  
  // Language options
  const availableLanguages = getAvailableLanguages();
  
  const languageContainer = document.createElement('div');
  languageContainer.style.cssText = 'display: flex; flex-direction: column; gap: 0.75rem; margin-bottom: 1.5rem;';
  
  availableLanguages.forEach(langCode => {
    const langButton = document.createElement('button');
    langButton.type = 'button';
    langButton.className = 'onboarding-language-option';
    langButton.style.cssText = 'text-align: left; padding: 1rem; border: 2px solid #d1d5db; border-radius: 0.5rem; background-color: white; cursor: pointer; transition: all 0.2s;';
    langButton.setAttribute('role', 'radio');
    langButton.setAttribute('aria-checked', 'false');
    langButton.setAttribute('tabindex', '0');
    
    const langLabel = document.createElement('div');
    langLabel.style.cssText = 'font-size: 1.125rem; font-weight: 600; color: #1f2937;';
    langLabel.textContent = getLanguageDisplayName(langCode);
    
    langButton.appendChild(langLabel);
    
    // Add hover effect
    langButton.addEventListener('mouseenter', () => {
      langButton.style.borderColor = '#93c5fd';
      langButton.style.backgroundColor = '#f0f9ff';
    });
    
    langButton.addEventListener('mouseleave', () => {
      langButton.style.borderColor = '#d1d5db';
      langButton.style.backgroundColor = 'white';
    });
    
    // Click moves to level selection
    langButton.addEventListener('click', () => {
      // Save language preference
      appState.preferences['learningLanguage'] = langCode;
      if (savePreferences) savePreferences();
      
      // Log language selection (using existing difficulty event as pattern)
      const prefEventType = EVENT_TYPES['PREFERENCE_DIFFICULTY_SET'];
      if (prefEventType) {
        logEvent(prefEventType, { language: langCode });
      }
      
      // Remove current modal and show level selection
      document.body.removeChild(overlay);
      showLevelSelectionModal();
    });
    
    langButton.addEventListener('focus', () => {
      langButton.style.outline = '2px solid #3b82f6';
      langButton.style.outlineOffset = '2px';
    });
    
    langButton.addEventListener('blur', () => {
      langButton.style.outline = 'none';
    });
    
    languageContainer.appendChild(langButton);
  });
  
  modal.appendChild(languageContainer);
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  
  // ESC to close (but require confirmation)
  const handleKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
    }
  };
  
  overlay.addEventListener('keydown', handleKeyDown);
  
  // Prevent clicking overlay to close (require confirmation)
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      e.preventDefault();
    }
  });
}

/**
 * Show level selection modal - Step 2 of onboarding
 */
function showLevelSelectionModal(): void {
  // Create modal overlay
  const overlay = document.createElement('div');
  overlay.id = 'onboarding-overlay';
  overlay.style.cssText = 'position: fixed; top: 0; left: 0; right: 0; bottom: 0; background-color: rgba(0, 0, 0, 0.5); z-index: 9999; display: flex; align-items: center; justify-content: center; padding: 1rem;';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-labelledby', 'onboarding-title');
  
  // Create modal content
  const modal = document.createElement('div');
  modal.style.cssText = 'background-color: white; border-radius: 0.5rem; padding: 2rem; max-width: 500px; width: 100%; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);';
  
  const title = document.createElement('h2');
  title.id = 'onboarding-title';
  title.textContent = 'Choose your level';
  title.style.cssText = 'font-size: 1.5rem; font-weight: 600; margin-bottom: 1.5rem; color: #1f2937;';
  modal.appendChild(title);
  
  // Level options
  const levels: Array<{ value: DifficultyLevel; label: string; description: string }> = [
    { value: 'A2', label: 'A2', description: 'Elementary: Basic phrases and simple conversations' },
    { value: 'B1', label: 'B1', description: 'Intermediate: Everyday situations and familiar topics' },
    { value: 'B2', label: 'B2', description: 'Upper-Intermediate: Complex topics and abstract ideas' }
  ];
  
  let selectedLevel: DifficultyLevel | null = null; // No default selection
  
  const levelContainer = document.createElement('div');
  levelContainer.style.cssText = 'display: flex; flex-direction: column; gap: 0.75rem; margin-bottom: 1.5rem;';
  
  levels.forEach(level => {
    const levelButton = document.createElement('button');
    levelButton.type = 'button';
    levelButton.className = 'onboarding-level-option';
    levelButton.style.cssText = 'text-align: left; padding: 1rem; border: 2px solid #d1d5db; border-radius: 0.5rem; background-color: white; cursor: pointer; transition: all 0.2s;';
    levelButton.setAttribute('role', 'radio');
    levelButton.setAttribute('aria-checked', 'false');
    levelButton.setAttribute('tabindex', '0');
    
    const levelLabel = document.createElement('div');
    levelLabel.style.cssText = 'font-size: 1.125rem; font-weight: 600; margin-bottom: 0.25rem; color: #1f2937;';
    levelLabel.textContent = level.label;
    
    const levelDesc = document.createElement('div');
    levelDesc.style.cssText = 'font-size: 0.875rem; color: #6b7280;';
    levelDesc.textContent = level.description;
    
    levelButton.appendChild(levelLabel);
    levelButton.appendChild(levelDesc);
    
    // Add hover effect
    levelButton.addEventListener('mouseenter', () => {
      levelButton.style.borderColor = '#93c5fd';
      levelButton.style.backgroundColor = '#f0f9ff';
    });
    
    levelButton.addEventListener('mouseleave', () => {
      // Restore to default (not selected state)
      levelButton.style.borderColor = '#d1d5db';
      levelButton.style.backgroundColor = 'white';
    });
    
    // Click immediately confirms and closes modal
    levelButton.addEventListener('click', () => {
      selectedLevel = level.value;
      
      // Save preference
      appState.preferences['cefrLevel'] = selectedLevel;
      if (savePreferences) savePreferences();
      
      // Mark onboarding as completed
      localStorage.setItem('listen-up-onboarding-completed', '1');
      
      // Log completion event
      const completedEventType = EVENT_TYPES['ONBOARDING_COMPLETED'];
      if (completedEventType) {
        logEvent(completedEventType, { level: selectedLevel });
      }
      const prefEventType = EVENT_TYPES['PREFERENCE_DIFFICULTY_SET'];
      if (prefEventType) {
        logEvent(prefEventType, { level: selectedLevel });
      }
      
      // Regenerate daily set with new level
      if (generateDailySet) generateDailySet('onboarding');
      
      // Remove modal
      document.body.removeChild(overlay);
      
      // Refresh library view if in library mode
      if (appState.mode === 'library') {
        if (renderLibraryView) renderLibraryView();
      }
    });
    
    levelButton.addEventListener('focus', () => {
      levelButton.style.outline = '2px solid #3b82f6';
      levelButton.style.outlineOffset = '2px';
    });
    
    levelButton.addEventListener('blur', () => {
      levelButton.style.outline = 'none';
    });
    
    levelContainer.appendChild(levelButton);
  });
  
  modal.appendChild(levelContainer);
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  
  // ESC to close (but require confirmation)
  const handleKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
    }
  };
  
  overlay.addEventListener('keydown', handleKeyDown);
  
  // Prevent clicking overlay to close (require confirmation)
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      e.preventDefault();
    }
  });
}
